import { Controller } from "@hotwired/stimulus"

// Organizer tools for a season live in one right-side drawer.
//
// Behaviour matches the session/lap detail drawer (`drawer_controller`):
// hovering the collapsed rail *peeks* — the column widens a little and the real
// panel fades in behind it at low opacity, so you can see what's in there
// before committing — and clicking expands it properly.
//
// Two things it adds over that drawer:
//
//   * **Resize.** When expanded the width is owned by `resizable-sidebar`
//     (edge="start", since a right-hand drawer drags its LEFT edge). This
//     controller reads the same storage key so it can restore the organizer's
//     chosen width on expand, and hands control back on collapse.
//   * **Tabs**, whose active one is persisted alongside the open state.
//
// Both are keyed by season id so two seasons don't share state. Persisting
// matters more here than usual: every control inside the drawer submits and
// re-renders, which is exactly what erases un-persisted UI state.
const COLLAPSED_PX = 40
const PEEK_PX = 48
const DEFAULT_PX = 372
const MIN_PX = 320
const MAX_PX = 640

export default class extends Controller {
  static targets = [
    "column",
    "rail",
    "panel",
    "peek",
    "tab",
    "section",
    "handle",
    "widthLabel",
  ]
  static values = { id: String }

  connect() {
    this.closeOnEscape = this.closeOnEscape.bind(this)
    this.expanded = localStorage.getItem(this.openKey) === "true"
    this.peeking = false
    this.apply({ animate: false })
    this.showTab(localStorage.getItem(this.tabKey) || this.defaultTab)
    this.watchWidth()
  }

  disconnect() {
    document.removeEventListener("keydown", this.closeOnEscape)
    this.observer?.disconnect()
  }

  // ───── width ─────

  // The Plan tab has three width breakpoints (the training summary, the row
  // tools, then the date column all drop out). They're the drawer's width, not
  // the viewport's, so a CSS container query is the obvious tool — and the wrong
  // one: `container-type: inline-size` implies `contain: layout`, which makes
  // this element a containing block for `position: fixed` descendants, and every
  // picker in here is a fixed panel that deliberately escapes its ancestors.
  //
  // Observing the width and stamping an attribute gets the same breakpoints with
  // no containment. It also catches every width change regardless of who made
  // it — this controller on expand, `resizable-sidebar` mid-drag, or the window.
  watchWidth() {
    if (typeof ResizeObserver === "undefined") return
    this.observer = new ResizeObserver(([entry]) => {
      this.applyWidth(entry.contentRect.width)
    })
    this.observer.observe(this.columnTarget)
  }

  applyWidth(width) {
    // The collapsed rail reports 40px, and a hidden panel 0 — neither is a
    // drawer width, and stamping them would flag every breakpoint at once.
    if (width < 200) return

    const flags = []
    if (width <= 430) flags.push("narrow")
    if (width <= 345) flags.push("tiny")
    this.columnTarget.dataset.drawerW = flags.join(" ")

    if (this.hasWidthLabelTarget) {
      this.widthLabelTarget.textContent = `${Math.round(width)} px · drag the edge`
    }
  }

  // ───── open / closed ─────

  toggle() {
    this.expanded = !this.expanded
    localStorage.setItem(this.openKey, this.expanded)
    this.apply({ animate: true })
  }

  open() {
    if (this.expanded) return
    this.expanded = true
    localStorage.setItem(this.openKey, "true")
    this.apply({ animate: true })
  }

  close() {
    if (!this.expanded) return
    this.expanded = false
    localStorage.setItem(this.openKey, "false")
    this.apply({ animate: true })
  }

  // ───── peek ─────

  // The peek shows the *tab names*, not whichever tab was last open: at 48px a
  // fading round list is unreadable noise, and "what's in there?" is answered by
  // five words, not four truncated track names.
  peekShow() {
    if (this.expanded || this.peeking) return
    this.peeking = true
    this.columnTarget.style.width = `${PEEK_PX}px`

    // Render at opacity-0, then transition up on the next frame so the fade
    // actually animates rather than snapping.
    const peek = this.peekTarget
    peek.classList.remove("hidden", "opacity-20", "opacity-100")
    peek.classList.add("flex", "opacity-0")
    this.railTarget.classList.add("hidden")

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (!this.peeking) return
        peek.classList.remove("opacity-0")
        peek.classList.add("opacity-20")
      })
    })
  }

  peekHide() {
    if (this.expanded || !this.peeking) return
    this.peeking = false
    this.columnTarget.style.width = `${COLLAPSED_PX}px`
    this.peekTarget.classList.remove("opacity-20")
    this.peekTarget.classList.add("opacity-0")
    this._hidePeekOnTransitionEnd()
  }

  _hidePeekOnTransitionEnd() {
    const swap = () => {
      if (this.expanded || this.peeking) return
      this._hidePeek()
    }
    this.columnTarget.addEventListener("transitionend", swap, { once: true })
  }

  _hidePeek() {
    this.peekTarget.classList.add("hidden")
    this.peekTarget.classList.remove(
      "flex",
      "opacity-0",
      "opacity-20",
      "opacity-100",
    )
    this.railTarget.classList.remove("hidden")
  }

  expandFromPeek() {
    if (!this.peeking) return
    this.toggle()
  }

  // ───── tabs ─────

  // Controls elsewhere on the page can jump straight to a tab — the
  // "Penalties" link under the championship table, for instance.
  openPenalties(event) {
    event?.preventDefault()
    this.open()
    this.select("penalties")
  }

  selectTab(event) {
    this.select(event.currentTarget.dataset.seasonDrawerTab)
  }

  select(name) {
    localStorage.setItem(this.tabKey, name)
    this.showTab(name)
  }

  showTab(name) {
    const known = this.sectionTargets.some(
      (s) => s.dataset.seasonDrawerTab === name,
    )
    const active = known ? name : this.defaultTab

    this.sectionTargets.forEach((section) => {
      section.classList.toggle(
        "hidden",
        section.dataset.seasonDrawerTab !== active,
      )
    })
    this.tabTargets.forEach((tab) => {
      const on = tab.dataset.seasonDrawerTab === active
      tab.classList.toggle("bg-app-surface-active", on)
      tab.classList.toggle("text-app-text", on)
      tab.classList.toggle("text-app-text-muted", !on)
      tab.setAttribute("aria-selected", on ? "true" : "false")
    })
  }

  // ───── plumbing ─────

  apply({ animate }) {
    this.peeking = false
    const col = this.columnTarget

    if (!animate) col.classList.remove("transition-[width]")

    if (this.expanded) {
      col.style.width = `${this.storedWidth}px`
      this._showPanel("opacity-100")
      document.addEventListener("keydown", this.closeOnEscape)
    } else {
      col.style.width = `${COLLAPSED_PX}px`
      if (animate) this._hidePanelOnTransitionEnd()
      else this._hidePanelImmediate()
      document.removeEventListener("keydown", this.closeOnEscape)
    }

    // The drag handle is only meaningful while expanded — a 40px rail has
    // nothing to resize, and leaving it live would let you drag the collapsed
    // rail open sideways.
    if (this.hasHandleTarget)
      this.handleTarget.classList.toggle("hidden", !this.expanded)

    if (!animate)
      requestAnimationFrame(() => col.classList.add("transition-[width]"))
  }

  _showPanel(opacityClass) {
    if (this.hasPeekTarget) this._hidePeek()
    const panel = this.panelTarget
    panel.classList.remove("hidden", "opacity-0", "opacity-20", "opacity-100")
    panel.classList.add("flex", opacityClass)
    this.railTarget.classList.add("hidden")
  }

  _hidePanelImmediate() {
    const panel = this.panelTarget
    panel.classList.add("hidden")
    panel.classList.remove("flex", "opacity-0", "opacity-20", "opacity-100")
    if (this.hasPeekTarget) this.peekTarget.classList.add("hidden")
    this.railTarget.classList.remove("hidden")
  }

  _hidePanelOnTransitionEnd() {
    const swap = () => {
      if (this.expanded || this.peeking) return
      this._hidePanelImmediate()
    }
    this.columnTarget.addEventListener("transitionend", swap, { once: true })
  }

  closeOnEscape(event) {
    // Never steal Escape from a field the organizer is typing in, or from the
    // confirm dialog stacked above the drawer.
    if (event.key !== "Escape" || !this.expanded) return
    if (document.querySelector("dialog[open]")) return

    const tag = document.activeElement?.tagName
    if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return

    this.close()
  }

  // Whatever width the organizer last dragged to, clamped to the same bounds
  // the resize controller enforces. Shares its storage key.
  get storedWidth() {
    const raw = parseInt(localStorage.getItem(this.widthKey), 10)
    if (!Number.isFinite(raw)) return DEFAULT_PX
    return Math.min(MAX_PX, Math.max(MIN_PX, raw))
  }

  get defaultTab() {
    return this.sectionTargets[0]?.dataset.seasonDrawerTab || "rounds"
  }

  get openKey() {
    return `season-drawer:${this.idValue}:open`
  }

  get tabKey() {
    return `season-drawer:${this.idValue}:tab`
  }

  get widthKey() {
    return `season-drawer:${this.idValue}:width`
  }
}
